import { useMemo, useState, useEffect } from "react";
import { useCollectionDashboardData } from "./useDashboardMetricsDataUtils";
import {
  extractMetricsFromDashboardData,
  processDashboardMetrics,
  processDirectInput,
  convertToQuadrantData,
  convertToFairMarketData,
  convertToBuySellData,
} from "./useDashboardMetricsDataUtils";
import type { DashboardMetricsDataPoint } from "./useDashboardMetricsDataUtils";
import type { DashboardMetricsGroupType } from "types/dashboardMetrics";
import type { DirectQuadrantInput, QuadrantDataPoint } from "screens/landing/tabs/collections/chartComponents/data/quadrantData";
import type { FairMarketChartData } from "screens/landing/tabs/collections/chartComponents/FairMarketChart/FairMarketChart";
import type { BuySellHoldChartItem } from "screens/landing/tabs/collections/chartComponents/BuySellHoldChart/BuySellHoldChart";

const MAX_IMMEDIATE_PROJECTS = 10; // Process up to 10 projects immediately
const BATCH_SIZE = 10; // Batch size for additional projects

interface DashboardMetricsDataStore {
  [projectId: string]: DashboardMetricsDataPoint;
}

// Generic hook that handles all dashboard metrics group types efficiently
export const useDashboardMetricsData = (
  groupType: DashboardMetricsGroupType,
  projectIds?: string | string[],
  singleProjectData?: DirectQuadrantInput | DirectQuadrantInput[]
): {
  data: DashboardMetricsDataPoint[];
  quadrantData: QuadrantDataPoint[];
  fairMarketData: FairMarketChartData[];
  buySellData: { items: BuySellHoldChartItem[] };
  isLoading: boolean;
  progress: number;
  loadedCount: number;
  totalCount: number;
} => {
  // Normalize input
  const normalizedProjectIds = useMemo(() => {
    if (!projectIds) return [];
    return Array.isArray(projectIds) ? projectIds : [projectIds];
  }, [projectIds]);

  // Split into immediate and batched processing
  const immediateProjectIds = useMemo(() => normalizedProjectIds.slice(0, MAX_IMMEDIATE_PROJECTS), [normalizedProjectIds]);

  const batchedProjectIds = useMemo(() => normalizedProjectIds.slice(MAX_IMMEDIATE_PROJECTS), [normalizedProjectIds]);

  // State for batched processing
  const [batchStore, setBatchStore] = useState<DashboardMetricsDataStore>({});
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [isProcessingBatches, setIsProcessingBatches] = useState(false);

  // Process immediate projects (up to 10) using individual hooks
  const project1Data = useCollectionDashboardData(immediateProjectIds[0]);
  const project2Data = useCollectionDashboardData(immediateProjectIds[1]);
  const project3Data = useCollectionDashboardData(immediateProjectIds[2]);
  const project4Data = useCollectionDashboardData(immediateProjectIds[3]);
  const project5Data = useCollectionDashboardData(immediateProjectIds[4]);
  const project6Data = useCollectionDashboardData(immediateProjectIds[5]);
  const project7Data = useCollectionDashboardData(immediateProjectIds[6]);
  const project8Data = useCollectionDashboardData(immediateProjectIds[7]);
  const project9Data = useCollectionDashboardData(immediateProjectIds[8]);
  const project10Data = useCollectionDashboardData(immediateProjectIds[9]);

  // Process immediate data
  const immediateData = useMemo(() => {
    const allCollectionData = [
      { id: immediateProjectIds[0], data: project1Data },
      { id: immediateProjectIds[1], data: project2Data },
      { id: immediateProjectIds[2], data: project3Data },
      { id: immediateProjectIds[3], data: project4Data },
      { id: immediateProjectIds[4], data: project5Data },
      { id: immediateProjectIds[5], data: project6Data },
      { id: immediateProjectIds[6], data: project7Data },
      { id: immediateProjectIds[7], data: project8Data },
      { id: immediateProjectIds[8], data: project9Data },
      { id: immediateProjectIds[9], data: project10Data },
    ].filter((item) => item.id); // Only process actual project IDs

    return allCollectionData
      .map(({ id, data }) => {
        if (!data.dashboardMetrics) return null;

        const processedMetrics = extractMetricsFromDashboardData(data.dashboardMetrics, groupType);
        if (processedMetrics.length === 0) return null;

        return processDashboardMetrics(processedMetrics, groupType, data.collectionName, id, data.ticker);
      })
      .filter((item): item is DashboardMetricsDataPoint => item !== null);
  }, [
    immediateProjectIds,
    project1Data,
    project2Data,
    project3Data,
    project4Data,
    project5Data,
    project6Data,
    project7Data,
    project8Data,
    project9Data,
    project10Data,
    groupType,
  ]);

  // Create batches for remaining projects
  const batches = useMemo(() => {
    const result: string[][] = [];
    for (let i = 0; i < batchedProjectIds.length; i += BATCH_SIZE) {
      result.push(batchedProjectIds.slice(i, i + BATCH_SIZE));
    }
    return result;
  }, [batchedProjectIds]);

  // Process current batch
  const currentBatch = useMemo(() => batches[currentBatchIndex] || [], [batches, currentBatchIndex]);

  // Call hooks for current batch (always call same number of hooks)
  const batchData1 = useCollectionDashboardData(currentBatch[0]);
  const batchData2 = useCollectionDashboardData(currentBatch[1]);
  const batchData3 = useCollectionDashboardData(currentBatch[2]);
  const batchData4 = useCollectionDashboardData(currentBatch[3]);
  const batchData5 = useCollectionDashboardData(currentBatch[4]);
  const batchData6 = useCollectionDashboardData(currentBatch[5]);
  const batchData7 = useCollectionDashboardData(currentBatch[6]);
  const batchData8 = useCollectionDashboardData(currentBatch[7]);
  const batchData9 = useCollectionDashboardData(currentBatch[8]);
  const batchData10 = useCollectionDashboardData(currentBatch[9]);

  // Memoize the array to prevent unnecessary re-renders
  const currentBatchData = useMemo(
    () => [batchData1, batchData2, batchData3, batchData4, batchData5, batchData6, batchData7, batchData8, batchData9, batchData10],
    [batchData1, batchData2, batchData3, batchData4, batchData5, batchData6, batchData7, batchData8, batchData9, batchData10]
  );

  // Process batch data and update store
  useEffect(() => {
    if (currentBatch.length === 0) return;

    const batchResults: DashboardMetricsDataPoint[] = [];

    currentBatch.forEach((projectId, index) => {
      const collectionData = currentBatchData[index];
      if (!collectionData?.dashboardMetrics) return;

      const processedMetrics = extractMetricsFromDashboardData(collectionData.dashboardMetrics, groupType);
      if (processedMetrics.length === 0) return;

      const result = processDashboardMetrics(processedMetrics, groupType, collectionData.collectionName, projectId, collectionData.ticker);

      if (result) batchResults.push(result);
    });

    if (batchResults.length > 0) {
      setBatchStore((prevStore) => {
        const newStore = { ...prevStore };
        batchResults.forEach((dataPoint) => {
          if (dataPoint.projectId) {
            newStore[dataPoint.projectId] = dataPoint;
          }
        });
        return newStore;
      });

      // Move to next batch
      if (currentBatchIndex < batches.length - 1) {
        const timer = setTimeout(() => {
          setCurrentBatchIndex((prev) => prev + 1);
        }, 50); // Reduced delay for better performance
        return () => clearTimeout(timer);
      } else {
        setIsProcessingBatches(false);
      }
    }
  }, [currentBatch, currentBatchData, currentBatchIndex, batches.length, groupType]);

  // Start batch processing when needed
  useEffect(() => {
    if (batches.length > 0 && !isProcessingBatches) {
      setIsProcessingBatches(true);
      setCurrentBatchIndex(0);
      setBatchStore({});
    }
  }, [batches.length, isProcessingBatches]);

  // Process direct input data
  const directInputData = useMemo(() => {
    if (!singleProjectData) return [];

    const inputs = Array.isArray(singleProjectData) ? singleProjectData : [singleProjectData];
    return inputs.map((input) => processDirectInput(input)).filter((item): item is DashboardMetricsDataPoint => item !== null);
  }, [singleProjectData]);

  // Combine all data
  const combinedData = useMemo(() => {
    const batchedData = batchedProjectIds
      .map((projectId) => batchStore[projectId])
      .filter((item): item is DashboardMetricsDataPoint => item !== undefined);

    // Priority: direct input > immediate data > batched data
    if (directInputData.length > 0) return directInputData;
    return [...immediateData, ...batchedData];
  }, [directInputData, immediateData, batchedProjectIds, batchStore]);

  // Convert to specific chart data formats
  const quadrantData = useMemo(() => convertToQuadrantData(combinedData), [combinedData]);
  const fairMarketData = useMemo(() => convertToFairMarketData(combinedData), [combinedData]);
  const buySellData = useMemo(() => convertToBuySellData(combinedData), [combinedData]);

  // Calculate progress
  const loadedCount = immediateData.length + Object.keys(batchStore).length;
  const totalCount = normalizedProjectIds.length;
  const progress = totalCount > 0 ? loadedCount / totalCount : 0;
  const isLoading = isProcessingBatches && loadedCount < totalCount;

  return {
    data: combinedData,
    quadrantData,
    fairMarketData,
    buySellData,
    isLoading,
    progress,
    loadedCount,
    totalCount,
  };
};

// Simplified hook for immediate results only (no batching)
export const useImmediateDashboardMetricsData = (
  groupType: DashboardMetricsGroupType,
  projectIds?: string | string[]
): DashboardMetricsDataPoint[] => {
  const normalizedProjectIds = useMemo(() => {
    if (!projectIds) return [];
    const ids = Array.isArray(projectIds) ? projectIds : [projectIds];
    return ids.slice(0, MAX_IMMEDIATE_PROJECTS); // Only take first 10
  }, [projectIds]);

  // Use the optimized hook but only return immediate data
  const { data } = useDashboardMetricsData(groupType, normalizedProjectIds);
  return data;
};
